/*
 * groovi-crosssell.js — by Studio Lab
 *
 * Cross-sell card carousel inside the PDP main section. Mirrors Horizon's
 * <product-recommendations> hydration pattern (intersect → fetch main via
 * Section Rendering API → morph) so `recommendations.products` populates
 * on first scroll-into-view. The block then renders cards server-side and
 * this script wires:
 *   - paged carousel (transform-based, one card per viewport)
 *   - variant <select> → updates visible price + installments via the
 *     per-card JSON blob (no extra fetches)
 *   - bag CTA → /cart/add.js + CartLinesUpdateEvent (drawer opens unchanged)
 */

import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class YunaCrosssell extends Component {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {number} */
  #currentIndex = 0;
  /** @type {boolean} */
  #wired = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.recommendationsPerformed === 'true') {
      this.#wireUI();
    } else {
      this.#intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          this.#loadRecommendations();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.#intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#activeFetch?.abort();
  }

  #loadRecommendations() {
    const { productId, sectionId, intent, url } = this.dataset;
    if (!productId || !sectionId || !url) return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const fullUrl = `${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    fetch(fullUrl, { signal: this.#activeFetch.signal })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`status ${res.status}`))))
      .then(async (html) => {
        if (!html?.trim().length) return;
        this.dataset.recommendationsPerformed = 'true';
        await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
        // morph in hydration mode patches THIS element's children in-place
        // (same node identity, no re-connect), so wire UI on the new DOM here.
        this.#wireUI();
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.warn('[groovi-crosssell] failed to load recommendations:', err);
        this.classList.add('hidden');
      })
      .finally(() => {
        this.#activeFetch = null;
      });
  }

  #wireUI() {
    if (this.#wired) return;
    this.#wired = true;

    const cards = this.querySelectorAll('[data-card]');
    if (!cards.length) return;

    this.#wireCarousel(cards.length);
    cards.forEach((card) => this.#wireCard(/** @type {HTMLElement} */ (card)));
  }

  /**
   * @param {number} slideCount
   */
  #wireCarousel(slideCount) {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    const counter = this.querySelector('[data-counter]');
    const nav = /** @type {HTMLElement | null} */ (this.querySelector('[data-nav]'));

    if (!track) return;

    if (slideCount <= 1) {
      // Owner direction 2026-06-19: with a single item, keep the controls VISIBLE
      // but disabled (→ opacity .3 via CSS) so it reads as "nothing more to
      // scroll" instead of the arrows vanishing. Hide the nav only when empty.
      if (slideCount === 0) {
        nav?.setAttribute('hidden', '');
        return;
      }
      nav?.removeAttribute('hidden');
      prev?.setAttribute('disabled', '');
      next?.setAttribute('disabled', '');
      if (counter) counter.textContent = `1/${slideCount}`;
      return;
    }
    nav?.removeAttribute('hidden');

    const updateUI = () => {
      track.style.transform = `translateX(-${this.#currentIndex * 100}%)`;
      if (counter) counter.textContent = `${this.#currentIndex + 1}/${slideCount}`;
      if (prev) prev.toggleAttribute('disabled', this.#currentIndex === 0);
      if (next) next.toggleAttribute('disabled', this.#currentIndex === slideCount - 1);
    };

    prev?.addEventListener('click', () => {
      if (this.#currentIndex > 0) {
        this.#currentIndex--;
        updateUI();
      }
    });

    next?.addEventListener('click', () => {
      if (this.#currentIndex < slideCount - 1) {
        this.#currentIndex++;
        updateUI();
      }
    });

    updateUI();
  }

  /**
   * @param {HTMLElement} card
   */
  #wireCard(card) {
    /** @type {Record<string, { price: string, installments: string, available: boolean }>} */
    let variants = {};
    const dataEl = card.querySelector('script[data-variants]');
    if (dataEl?.textContent) {
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.warn('[groovi-crosssell] bad variant JSON', e);
      }
    }

    const select = /** @type {HTMLSelectElement | null} */ (card.querySelector('[data-variant-select]'));
    const priceEl = card.querySelector('[data-price]');
    const installmentsEl = card.querySelector('[data-installments]');
    const addBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-add]'));

    select?.addEventListener('change', () => {
      const id = select.value;
      const v = variants[id];
      if (!v) return;
      if (priceEl) priceEl.textContent = v.price;
      if (installmentsEl) installmentsEl.innerHTML = v.installments;
      if (addBtn) {
        addBtn.dataset.variantId = id;
        addBtn.disabled = !v.available;
      }
    });

    addBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const variantId = addBtn.dataset.variantId;
      if (!variantId || addBtn.disabled || addBtn.getAttribute('aria-busy') === 'true') return;
      this.#addToCart(variantId, addBtn);
    });
  }

  /**
   * @param {string} variantId
   * @param {HTMLButtonElement} addBtn
   */
  #addToCart(variantId, addBtn) {
    // Re-entrancy guard: the data-added attribute lives for 800ms after a
    // successful click (see end of this method). Treat that window as
    // "in flight" so spam-clicks don't queue extra cart-adds.
    if (addBtn.dataset.added === 'true') return;
    this.cartAddTrigger = addBtn;
    this.querySelector('[data-cart-add-error]')?.remove();
    addBtn.disabled = true;
    addBtn.setAttribute('aria-busy', 'true');

    // Fire the click-feedback animation synchronously, mirroring the main
    // PDP button (assets/product-form.js:89 calls animateAddToCart() inside
    // handleClick — not after the fetch resolves). This gives instant
    // feedback regardless of network latency.
    addBtn.dataset.added = 'true';
    setTimeout(() => addBtn.removeAttribute('data-added'), 800);

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    const payload = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionIds.join(','),
    };

    // Horizon 4.x event system (upgrade playbook §5.2–§5.4): the cart event is a
    // CartLinesUpdateEvent carrying a deferred promise. We dispatch it BEFORE the
    // fetch, then resolve it with the FULL cart (currency-bearing) once /cart/add.js
    // returns — createCartFromAjaxResponse() throws without cart-level currency,
    // which /cart/add.js does NOT include, so we fetch /cart.js for it.
    // `Theme` is a bare global in 4.x (window.Theme is undefined — §5.12).
    const cartAddUrl =
      (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(async (response) => {
        if (response.status) {
          this.#showAddError(response.description || response.message);
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          // Clear the optimistic feedback so a retry click works immediately.
          addBtn.removeAttribute('data-added');
          return;
        }

        // Full cart for currency (§5.4); /cart/add.js response carries .sections only.
        const fullCart = await (await fetch('/cart.js')).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
          detail: {
            items: fullCart.items,
            sections: response.sections,
            source: 'groovi-crosssell',
            sourceId: this.id.toString(),
            itemCount: 1,
            productId: this.dataset.productId,
            didError: false,
          },
        });
      })
      .catch((err) => {
        console.warn('[groovi-crosssell] add-to-cart failed:', err);
        this.#showAddError();
        deferred.reject(err);
        // Clear the optimistic feedback so a retry click works immediately.
        addBtn.removeAttribute('data-added');
      })
      .finally(() => {
        if (addBtn.dataset.variantId === variantId) addBtn.disabled = false;
        addBtn.removeAttribute('aria-busy');
      });
  }

  #showAddError(message = 'Não foi possível confirmar a inclusão. Confira seu carrinho antes de tentar novamente.') {
    let error = this.querySelector('[data-cart-add-error]');
    if (!error) {
      error = document.createElement('p');
      error.setAttribute('data-cart-add-error', '');
      error.setAttribute('role', 'alert');
      this.appendChild(error);
    }
    error.textContent = message;
  }
}

if (!customElements.get('groovi-crosssell')) {
  customElements.define('groovi-crosssell', YunaCrosssell);
}
